import React, { useEffect, useState, useCallback, useRef } from "react";
import ReactECharts from "echarts-for-react";
import SelectOption from "./SelectOption";
import "../css/main.scss";
import { setAPIAuthorizationToken } from "../apis/API";
import Cookies from "js-cookie";
import Loading from "../pages/Loading";
import { get_user_list } from "../apis/GetUsers";
import { get_trend } from "../apis/GetTrend";
import { useTranslation } from "../contexts/LocaleContext";

// helper(s) removed — keep minimal until needed

const Main = () => {
  const { t } = useTranslation();
  // safe translator: if t(key) returns the key or contains dots, fall back to provided text
  const translate = (key, fallback) => {
    if (!t) return fallback;
    try {
      const res = t(key);
      if (!res) return fallback;
      if (
        typeof res === "string" &&
        (res.toLowerCase() === key.toLowerCase() || res.indexOf(".") !== -1)
      ) {
        return fallback;
      }
      return res;
    } catch (err) {
      return fallback;
    }
  };
  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [trendData, setTrendData] = useState(null);
  const [selectedUser, setSelectedUser] = useState("");
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split("T")[0];
  });
  // Loading state for trend API requests (shows Loading while fetching)
  const [isTrendLoading, setIsTrendLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const chartControlsRef = useRef(null);
  const [popupFixedStyle, setPopupFixedStyle] = useState(null);
  const echartsRef = useRef(null);

  // Get user list
  const getUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await get_user_list(1, 200, "", "");

      if (res && res.data && res.data.data) {
        setUsers(res.data.data);
      } else {
        setUsers([]);
      }
    } catch (err) {
      console.error("Failed to fetch users:", err);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Get trend data
  const getTrendData = useCallback(async () => {
    try {
      setIsTrendLoading(true);
      const starttime = new Date(startDate).toISOString();
      const endtime = new Date(endDate).toISOString();
      const res = await get_trend(starttime, endtime, selectedUser);

      if (res && res.data.data) {
        setTrendData(res.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch trend data:", err);
    } finally {
      setIsTrendLoading(false);
    }
  }, [startDate, endDate, selectedUser]);

  // Initialize token
  useEffect(() => {
    const token = Cookies.get("token");

    console.log(token);

    if (token) {
      setAPIAuthorizationToken(token);
      getUsers();
      getTrendData();
    }
  }, [getUsers, getTrendData]);

  // Handle user selection change
  const handleUserChange = (e) => {
    setSelectedUser(e.target.value);
  };

  // Handle date change
  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
  };

  // Re-fetch data when selection changes
  useEffect(() => {
    if (selectedUser !== undefined && startDate && endDate) {
      getTrendData();
    }
  }, [selectedUser, startDate, endDate, getTrendData]);

  // Position filter popup as fixed on small screens to avoid being clipped by overflow containers.
  useEffect(() => {
    const updatePopupPosition = () => {
      if (!showFilters) {
        setPopupFixedStyle(null);
        return;
      }
      const isSmall = window.innerWidth <= 768;
      if (!isSmall) {
        setPopupFixedStyle(null);
        return;
      }
      const el = chartControlsRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const top = rect.bottom + 8; // 8px gap
      const right = Math.max(12, window.innerWidth - rect.right);
      setPopupFixedStyle({
        position: "fixed",
        top: `${top}px`,
        right: `${right}px`,
        minWidth: "260px",
        maxWidth: "360px",
        zIndex: 10000,
      });
    };

    updatePopupPosition();
    window.addEventListener("resize", updatePopupPosition);
    window.addEventListener("scroll", updatePopupPosition, true);
    return () => {
      window.removeEventListener("resize", updatePopupPosition);
      window.removeEventListener("scroll", updatePopupPosition, true);
    };
  }, [showFilters]);

  // 柱状图数据
  const getChartOptions = () => {
    const generateTrendData = () => {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));

      const data = [];
      for (let i = 0; i <= days; i++) {
        const date = new Date(start);
        date.setDate(start.getDate() + i);

        const successCount = trendData?.es?.[1]?.count || 0;
        const failCount = trendData?.es?.[0]?.count || 0;

        const variation = Math.sin(i * 0.5) * 0.3 + 0.7;
        const success = Math.round(
          successCount * variation * (0.8 + Math.random() * 0.4)
        );
        const fail = Math.round(failCount * (0.2 + Math.random() * 0.3));

        data.push({
          date: date.toISOString().split("T")[0],
          success: success,
          fail: fail,
          total: success + fail,
        });
      }
      return data;
    };

    const trendDataPoints = generateTrendData();
    const isSmallScreen =
      typeof window !== "undefined" && window.innerWidth <= 768;
    const desiredLabels = isSmallScreen ? 4 : 10;
    const labelStep =
      trendDataPoints.length > desiredLabels
        ? Math.ceil(trendDataPoints.length / desiredLabels)
        : 1;

    const legendEntries = [
      translate("legend.success", "成功"),
      translate("legend.fail", "失败"),
    ];

    return {
      title: {
        show: false,
      },
      tooltip: {
        trigger: "axis",
        // on mobile use click to show tooltip (touch doesn't fire mousemove)
        triggerOn: isSmallScreen ? "click" : "mousemove",
        axisPointer: {
          type: "cross",
          label: {
            backgroundColor: "#6a7985",
          },
        },
        confine: true,
      },
      legend: {
        data: legendEntries,
        top: 30,
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: isSmallScreen ? 80 : "10%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: trendDataPoints.map((item) => item.date),
        axisLabel: {
          color: "#666",
          rotate: isSmallScreen ? 0 : 0,
          interval: Math.max(0, labelStep - 1),
          margin: 6,
          formatter: function (value) {
            try {
              if (typeof value === "string" && value.indexOf("-") !== -1) {
                const parts = value.split("-");
                return `${parts[1]}/${parts[2]}`;
              }
            } catch (err) {}
            return value;
          },
        },
        axisTick: { alignWithLabel: true },
      },
      yAxis: {
        type: "value",
        axisLabel: {
          color: "#666",
        },
      },
      series: (() => {
        // always render as line chart (no bar series)
        const seriesType = "line";
        const commonSeriesProps = (color) => ({
          type: seriesType,
          itemStyle: { color },
          emphasis: { focus: "series" },
        });

        return [
          {
            name: translate("legend.success", "成功"),
            ...commonSeriesProps("#36b37e"),
            data: trendDataPoints.map((item) => item.success),
            smooth: true,
            areaStyle: {},
          },
          {
            name: translate("legend.fail", "失败"),
            ...commonSeriesProps("#ef5350"),
            data: trendDataPoints.map((item) => item.fail),
            smooth: true,
            areaStyle: {},
          },
        ];
      })(),
    };
  };
  // Ensure echarts instance updates when data/date range changes
  useEffect(() => {
    try {
      const instance = echartsRef.current?.getEchartsInstance?.();
      if (!instance) return;
      // update options and force layout recalculation
      instance.setOption(getChartOptions(), true);
      instance.resize();
    } catch (err) {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendData, startDate, endDate, selectedUser]);

  const computeSummaryStats = () => {
    const successCount = trendData?.es?.[1]?.count || 0;
    const failCount = trendData?.es?.[0]?.count || 0;
    const totalCount = successCount + failCount;

    const successPct = totalCount
      ? ((successCount / totalCount) * 100).toFixed(1)
      : "0.0";
    const failPct = totalCount
      ? ((failCount / totalCount) * 100).toFixed(1)
      : "0.0";

    const start = new Date(startDate);
    const end = new Date(endDate);
    const days = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));

    const makeSeries = (baseCount) => {
      const series = [];
      for (let i = 0; i <= days; i++) {
        const variation = Math.sin(i * 0.5) * 0.3 + 0.7;
        series.push(Math.round(baseCount * variation));
      }
      return series;
    };

    const successSeries = makeSeries(successCount);
    const failSeries = makeSeries(failCount);

    const lastSuccess = successSeries[successSeries.length - 1] || 0;
    const prevSuccess =
      successSeries[successSeries.length - 2] || lastSuccess || 0;
    const lastFail = failSeries[failSeries.length - 1] || 0;
    const prevFail = failSeries[failSeries.length - 2] || lastFail || 0;

    const calcTrend = (last, prev) => {
      if (!prev) return 0;
      return ((last - prev) / (prev || 1)) * 100;
    };

    const successTrend = calcTrend(lastSuccess, prevSuccess);
    const failTrend = calcTrend(lastFail, prevFail);

    return {
      successCount,
      failCount,
      totalCount,
      successPct,
      failPct,
      successTrend: successTrend.toFixed(1),
      failTrend: failTrend.toFixed(1),
    };
  };

  const usertype = Number(Cookies.get("usertype"));

  return (
    <div className="dashboard main-dashboard">
      {isLoading || isTrendLoading ? (
        <Loading />
      ) : (
        <>
          <div className="trend-summary" style={{ marginTop: 16 }}>
            {(() => {
              const stats = computeSummaryStats();

              const summaryCards = [
                {
                  type: "success",
                  label: translate("summary.success", "成功"),
                  value: stats.successCount,
                  percent: stats.successPct,
                  trend: stats.successTrend,
                  path: "M0 18 C20 18 32 6 48 6 C64 6 80 20 100 18 L120 18",
                  stroke: "#3fc88a",
                },
                {
                  type: "fail",
                  label: translate("summary.fail", "失败"),
                  value: stats.failCount,
                  percent: stats.failPct,
                  trend: stats.failTrend,
                  path: "M0 18 C18 18 36 8 54 8 C72 8 90 22 108 18 L120 18",
                  stroke: "#ef5350",
                },
              ];

              const cards = summaryCards.map((card, index) => (
                <div key={card.type} className={`summary-card ${card.type}`}>
                  <div className="card-body">
                    <div className="card-header">
                      <div className="label">{card.label}</div>
                      <div className="card-percent primary">
                        {card.percent}%
                      </div>
                    </div>

                    <div className="card-row">
                      <div className="value"> {card.value}</div>

                      <div className="progress-wrapper" aria-hidden="true">
                        <div className="progress-track">
                          <div
                            className="progress-fill"
                            style={{
                              width: `${Number(card.percent)}%`,
                              background:
                                card.type === "success"
                                  ? "#36b37e"
                                  : card.type === "fail"
                                  ? "#ef5350"
                                  : "#f6b84b",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ));

              // pie chart options for today's success/fail ratio
              const pieOptions = {
                tooltip: { trigger: "item" },
                legend: {
                  orient: "vertical",
                  left: "left",
                  data: [
                    translate("legend.success", "成功"),
                    translate("legend.fail", "失败"),
                  ],
                },
                series: [
                  {
                    name: translate("legend.total", "总计"),
                    type: "pie",
                    radius: ["40%", "70%"],
                    avoidLabelOverlap: false,
                    label: { show: false, position: "center" },
                    emphasis: {
                      label: { show: true, fontSize: 14, fontWeight: "bold" },
                    },
                    data: [
                      {
                        value: stats.successCount,
                        name: translate("legend.success", "成功"),
                      },
                      {
                        value: stats.failCount,
                        name: translate("legend.fail", "失败"),
                      },
                    ],
                    color: ["#36b37e", "#ef5350"],
                  },
                ],
              };

              return (
                <>
                  <div className="summary-cards-column">{cards}</div>
                  <div
                    className="summary-pie"
                    style={{ width: 420, marginLeft: 12 }}
                  >
                    <ReactECharts
                      option={pieOptions}
                      style={{ height: 160, width: "100%" }}
                      notMerge={true}
                      lazyUpdate={true}
                      opts={{ renderer: "svg" }}
                    />
                  </div>
                </>
              );
            })()}
          </div>

          <div className="trend-chart" style={{ marginTop: 20 }}>
            <div className="chart-controls" ref={chartControlsRef}>
              <div
                className="date-range-display"
                role="status"
                aria-live="polite"
                aria-hidden="false"
              >
                <svg
                  data-prefix="fas"
                  data-icon="calendar-check"
                  className="svg-inline--fa fa-calendar-check text-gray-400"
                  role="img"
                  viewBox="0 0 448 512"
                  aria-hidden="true"
                >
                  <path
                    fill="currentColor"
                    d="M436 160H12c-6.627 0-12-5.373-12-12v-36c0-26.51 21.49-48 48-48h48V12c0-6.627 5.373-12 12-12h40c6.627 0 12 5.373 12 12v52h128V12c0-6.627 5.373-12 12-12h40c6.627 0 12 5.373 12 12v52h48c26.51 0 48 21.49 48 48v36c0 6.627-5.373 12-12 12zM12 192h424c6.627 0 12 5.373 12 12v260c0 26.51-21.49 48-48 48H48c-26.51 0-48-21.49-48-48V204c0-6.627 5.373-12 12-12zm333.296 95.947l-28.169-28.398c-4.667-4.705-12.265-4.736-16.97-.068L194.12 364.665l-45.98-46.352c-4.667-4.705-12.266-4.736-16.971-.068l-28.397 28.17c-4.705 4.667-4.736 12.265-.068 16.97l82.601 83.269c4.667 4.705 12.265 4.736 16.97.068l142.953-141.805c4.705-4.667 4.736-12.265.068-16.97z"
                  />
                </svg>

                <span className="date-range-text">
                  {startDate ? startDate.replace(/-/g, "/") : ""} -{" "}
                  {endDate ? endDate.replace(/-/g, "/") : ""}
                </span>
              </div>

              {usertype === 2 && (
                <>
                  <button
                    className="filter-toggle chart-toggle"
                    onClick={() => setShowFilters((s) => !s)}
                    aria-expanded={String(!!showFilters)}
                  >
                    <svg
                      data-prefix="fas"
                      data-icon="filter"
                      className="svg-inline--fa fa-filter text-xs"
                      role="img"
                      viewBox="0 0 512 512"
                      aria-hidden="true"
                    >
                      <path
                        fill="currentColor"
                        d="M487.976 0H24.028C2.71 0-8.047 25.866 7.058 40.971L192 225.941V432c0 7.831 3.821 15.17 10.237 19.662l80 55.98C298.02 518.69 320 507.493 320 487.98V225.941l184.947-184.97C520.021 25.896 509.338 0 487.976 0z"
                      />
                    </svg>

                    <span className="filter-text">
                      {translate("filters.title", "筛选")}
                    </span>
                    <span
                      className={`filter-caret ${showFilters ? "open" : ""}`}
                    >
                      ▾
                    </span>
                  </button>
                </>
              )}
              {showFilters && (
                <div
                  className="filter-card popup"
                  style={popupFixedStyle ? popupFixedStyle : undefined}
                >
                  <div className="filter-row">
                    <div className="filter-field">
                      <div className="filter-label">
                        {translate("filter.start_time", "开始时间")}
                      </div>
                      <input
                        type="date"
                        className="filter-input"
                        value={startDate}
                        onChange={handleStartDateChange}
                      />
                    </div>

                    <div className="filter-field">
                      <div className="filter-label">
                        {translate("filter.end_time", "结束时间")}
                      </div>
                      <input
                        type="date"
                        className="filter-input"
                        value={endDate}
                        onChange={handleEndDateChange}
                      />
                    </div>

                    <div className="filter-field">
                      <div className="filter-label">
                        {translate("filter.order_user", "订单用户")}
                      </div>
                      <SelectOption
                        value={selectedUser}
                        onChange={(v) => setSelectedUser(v)}
                        options={[
                          { value: "", label: translate("main.all_users", "所有用户") },
                          ...users.map((user, index) => ({
                            value: user.userName,
                            label: user.userName,
                          })),
                        ]}
                        ariaLabel="用户选择"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <ReactECharts
              ref={echartsRef}
              option={getChartOptions()}
              style={{ height: "400px", width: "100%" }}
              notMerge={true}
              lazyUpdate={true}
              opts={{ renderer: "svg" }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default Main;
